/**
 * Extraction-activity dashboard — Phase H0 #118.
 *
 * Admin-only. Surfaces the data captured by the DLP read audit
 * (data_read_log) so Compliance can spot anomalous reading volume per
 * user. Pairs with the per-user rate gate in app/services/dlp.py:
 *
 *   - Rate gate enforces hard limits (50 list / 200 detail per 15-min
 *     window per entity_type). Tripping the gate generates a BLOCKED
 *     row in data_read_log.
 *   - This dashboard aggregates the OK and BLOCKED counts over a
 *     configurable window (7 / 30 / 90 days), surfacing per-user p50
 *     vs. max-per-day and an anomaly_ratio (= max ÷ p50).
 *
 * Default sort by anomaly_ratio descending so the most unusual users
 * float to the top. Click a row to drill into that user's audit log.
 *
 * Pedro 2026-05-04: monthly review rhythm — Compliance hits this
 * page once a month; ratio > 5 is the orange threshold, > 10 is red.
 */
import {
  Badge, Container, Group, Pill, PillGroup, Select, Stack, Table, Text, Title,
} from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { auditApi, type ExtractionSummary, type ExtractionSummaryUser } from '../api/audit';
import { useCurrentUser } from '../hooks/useAuth';
import { BackToCompliance } from '../components/BackToCompliance';
import { AccessDeniedAlert } from '../components/RequireAccess';

const WINDOW_OPTS = [
  { value: '7',  label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
];

/**
 * Row tint based on anomaly_ratio. Mirrors the audit page's action
 * colour palette so a Compliance officer reading both pages doesn't
 * have to remap their colour intuition.
 */
function rowBgFor(ratio: number): string | undefined {
  if (ratio >= 10) return 'var(--mantine-color-red-1)';
  if (ratio >= 5) return 'var(--mantine-color-orange-1)';
  return undefined;
}

/**
 * Stable colour assignment for entity-type chips so the same entity
 * always gets the same colour across rows. Hash-of-name → palette
 * index. (Mantine pill colour tokens are limited to a small set; we
 * use the named ones directly.)
 */
const CHIP_COLOURS = ['blue', 'cyan', 'teal', 'violet', 'pink', 'grape', 'indigo'];
function chipColourFor(entityType: string): string {
  let h = 0;
  for (let i = 0; i < entityType.length; i++) h = (h * 31 + entityType.charCodeAt(i)) | 0;
  return CHIP_COLOURS[Math.abs(h) % CHIP_COLOURS.length];
}

export function ExtractionPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useCurrentUser();
  const [windowDays, setWindowDays] = useState<string>('30');

  const summaryQuery = useQuery<ExtractionSummary, Error>({
    queryKey: ['audit', 'extraction-summary', windowDays],
    queryFn: () => auditApi.extractionSummary(Number(windowDays)),
    enabled: currentUser?.role === 'admin',
    staleTime: 60_000,
  });

  // Already sorted server-side by total_reads_ok desc; we override
  // here to anomaly_ratio so unusual users float up. Users with no
  // reads at all (anomaly_ratio = 0) sink to the bottom.
  const sortedUsers = useMemo<ExtractionSummaryUser[]>(() => {
    if (!summaryQuery.data) return [];
    return [...summaryQuery.data.users].sort((a, b) => b.anomaly_ratio - a.anomaly_ratio);
  }, [summaryQuery.data]);

  if (currentUser && currentUser.role !== 'admin') {
    return <AccessDeniedAlert />;
  }

  const since = summaryQuery.data
    ? new Date(Date.now() - Number(windowDays) * 86400 * 1000).toISOString()
    : undefined;

  return (
    <Container size="xl">
      <Stack>
        <BackToCompliance />
        <Group justify="space-between">
          <Title order={2}>Data extraction activity</Title>
          <Select
            data={WINDOW_OPTS}
            value={windowDays}
            onChange={(v) => v && setWindowDays(v)}
            allowDeselect={false}
            w={180}
          />
        </Group>

        <Text c="dimmed" size="sm">
          Per-user read activity across regulated entities (Persons, Agreements, Accounts,
          Insurance) over the selected window. <b>Anomaly ratio</b> is each user's busiest
          day divided by their typical day; values above 5 (orange) or 10 (red) suggest
          investigation. Click a row to drill into that user's underlying audit log entries.
        </Text>

        {summaryQuery.isLoading && (
          <Text c="dimmed" size="sm">Loading…</Text>
        )}
        {summaryQuery.error && (
          <Text c="red" size="sm">{(summaryQuery.error as Error).message}</Text>
        )}

        {summaryQuery.data && (
          <Table withTableBorder striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>User</Table.Th>
                <Table.Th w={100} ta="right">Reads (OK)</Table.Th>
                <Table.Th w={100} ta="right">Blocked</Table.Th>
                <Table.Th>Top entity types</Table.Th>
                <Table.Th w={90} ta="right">p50 / day</Table.Th>
                <Table.Th w={90} ta="right">max / day</Table.Th>
                <Table.Th w={120} ta="right">Anomaly ratio</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {sortedUsers.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={7}>
                    <Text c="dimmed" size="sm" ta="center">
                      No regulated reads in the selected window.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {sortedUsers.map((u) => (
                <Table.Tr
                  key={u.user_id}
                  style={{
                    cursor: 'pointer',
                    backgroundColor: rowBgFor(u.anomaly_ratio),
                  }}
                  onClick={() => {
                    const params = new URLSearchParams({
                      actor_user_id: u.user_id,
                    });
                    if (since) params.set('since', since);
                    navigate(`/audit?${params.toString()}`);
                  }}
                >
                  <Table.Td>
                    <Text size="sm">{u.email}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" ff="monospace">{u.total_reads_ok.toLocaleString()}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    {u.total_reads_blocked > 0 ? (
                      <Badge color="red" variant="filled">{u.total_reads_blocked}</Badge>
                    ) : (
                      <Text size="xs" c="dimmed">0</Text>
                    )}
                  </Table.Td>
                  <Table.Td>
                    <PillGroup gap={4}>
                      {Object.entries(u.by_entity_type)
                        .sort((a, b) => b[1] - a[1])
                        .slice(0, 3)
                        .map(([etype, count]) => (
                          <Pill key={etype} size="sm" bg={`var(--mantine-color-${chipColourFor(etype)}-1)`}>
                            {etype} <Text span c="dimmed" size="xs">· {count}</Text>
                          </Pill>
                        ))}
                    </PillGroup>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" ff="monospace">{u.p50_reads_per_day}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Text size="sm" ff="monospace">{u.max_reads_per_day}</Text>
                  </Table.Td>
                  <Table.Td ta="right">
                    <Badge
                      color={u.anomaly_ratio >= 10 ? 'red' : u.anomaly_ratio >= 5 ? 'orange' : 'gray'}
                      variant={u.anomaly_ratio >= 5 ? 'filled' : 'light'}
                    >
                      {u.anomaly_ratio.toFixed(2)}×
                    </Badge>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        <Text c="dimmed" size="xs" mt="md">
          Source: <code>data_read_log</code> table, populated by every list/detail GET on
          regulated entities through <code>app/services/dlp.py</code>. Window:
          {' '}<b>{summaryQuery.data?.window_days ?? windowDays}</b> days.
          Generated: {summaryQuery.data?.generated_at_utc ?? '—'} UTC.
        </Text>
      </Stack>
    </Container>
  );
}
